// Wall-clock parts extracted for Eastern time - see easternDateParts below.
export type EasternDateParts = { year: number; month: number; day: number; hour: number; minute: number };

// Intl.DateTimeFormat instances are stateless (formatToParts takes the date
// as its argument), so a single formatter is safe to share across every
// easternDateParts call rather than reconstructing one per call - this runs
// once per chart point (up to 400 for a line chart) on every render.
const EASTERN_DATE_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric", month: "numeric", day: "numeric",
    hour: "numeric", minute: "numeric", hourCycle: "h23"
});

/**
 * Wall-clock components of `date` in Eastern Time (DST-aware via
 * Intl.DateTimeFormat). Shared by FmsChatDisplayConverter's parseTemporalCell
 * when re-projecting an explicit-offset instant to Eastern wall-clock time.
 * getEasternOffsetMinutes below intentionally keeps its own parts-extraction
 * rather than using this helper - it needs seconds precision, which this
 * formatter does not expose.
 */
export function easternDateParts(date: Date): EasternDateParts {
    const parts = EASTERN_DATE_PARTS_FORMATTER.formatToParts(date);
    const partValue = (type: string): number => Number(parts.find(p => p.type === type)?.value);
    return {
        year: partValue("year"), month: partValue("month"), day: partValue("day"),
        hour: partValue("hour"), minute: partValue("minute")
    };
}

/**
 * Get the current Eastern Time UTC offset in minutes.
 * Accounts for EST (-300) vs EDT (-240) automatically.
 */
function getEasternOffsetMinutes(): number {
    const now = new Date();
    const eastern = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: false,
    }).formatToParts(now);

    const parts: Record<string, string> = {};
    eastern.forEach(p => { parts[p.type] = p.value; });

    const etDate = new Date(
        `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`
    );
    return Math.round((now.getTime() - etDate.getTime()) / 60000);
}

/** Convert a UTC Date to the equivalent wall-clock Date in Eastern Time. */
export function utcToEastern(utc: Date): Date {
    const offsetMs = getEasternOffsetMinutes() * 60000;
    return new Date(utc.getTime() - offsetMs);
}

/** Interpret a Date's wall-clock values as Eastern Time and convert to UTC. */
export function easternToUtc(eastern: Date): Date {
    const offsetMs = getEasternOffsetMinutes() * 60000;
    return new Date(eastern.getTime() + offsetMs);
}
